// Small, dependency-free sound controls shared by the pond and its art preview.
import { sound } from '../pond/sound';

let controlId = 0;

/** Creation never enables sound. Preview samples are optional and respect mute/pause/volume. */
export function createSoundControls(options: { samples?: boolean } = {}): HTMLElement {
  const root = document.createElement('div');
  root.className = 'sound-controls';
  root.setAttribute('role', 'group');
  root.setAttribute('aria-label', 'Pond sound');
  root.style.cssText = 'display:flex;align-items:center;gap:6px;position:relative';

  const toggle = document.createElement('button');
  toggle.type = 'button'; toggle.className = 'btn secondary sound-toggle';
  const settings = document.createElement('details'); settings.className = 'sound-settings';
  const summary = document.createElement('summary');
  summary.textContent = '♫'; summary.title = 'Sound settings';
  summary.setAttribute('aria-label', 'Sound settings');
  summary.style.cssText = 'cursor:pointer;padding:9px 12px;border-radius:999px;background:var(--paper,#fffdf5);color:var(--ink,#163a2b);list-style:none';
  const menu = document.createElement('div');
  menu.className = 'sound-menu';
  menu.style.cssText = 'position:absolute;right:0;top:calc(100% + 8px);z-index:70;width:230px;padding:14px;border-radius:16px;background:var(--paper,#fffdf5);color:var(--ink,#163a2b);box-shadow:0 8px 24px #163a2b33;display:grid;gap:12px';

  const label = document.createElement('label'); label.textContent = 'Pond volume';
  const slider = document.createElement('input');
  slider.id = `pond-volume-${++controlId}`; label.htmlFor = slider.id;
  slider.type = 'range'; slider.min = '0'; slider.max = '100'; slider.step = '1';
  slider.value = String(Math.round(sound.volume * 100));
  slider.setAttribute('aria-label', 'Pond volume');
  slider.style.cssText = 'width:100%;accent-color:var(--pad,#3fa34d)';
  const value = document.createElement('output'); value.htmlFor.add(slider.id);
  value.style.cssText = 'font-size:13px;justify-self:end';
  const volume = document.createElement('div'); volume.append(label, slider, value);

  const pause = document.createElement('button'); pause.type = 'button'; pause.className = 'btn secondary small';
  const hint = document.createElement('p');
  hint.style.cssText = 'font-size:12px;line-height:1.4;margin:0;color:var(--muted,#5e7a6a)';
  const samples: HTMLButtonElement[] = [];
  const update = () => {
    const available = sound.isAvailable;
    toggle.disabled = !available;
    toggle.textContent = available ? sound.isMuted ? '♬ Sound off' : '♬ Sound on' : 'Sound unavailable';
    toggle.setAttribute('aria-label', available ? sound.isMuted ? 'Enable pond sound' : 'Mute pond sound' : 'Pond sound is unavailable in this browser');
    toggle.setAttribute('aria-pressed', String(!sound.isMuted));
    slider.disabled = !available;
    pause.disabled = !available || sound.isScenePaused;
    slider.value = String(Math.round(sound.volume * 100));
    slider.setAttribute('aria-valuetext', `${Math.round(sound.volume * 100)} percent`);
    value.value = `${Math.round(sound.volume * 100)}%`;
    pause.textContent = sound.isPaused ? 'Resume pond sound' : 'Pause pond sound';
    pause.setAttribute('aria-pressed', String(sound.isPaused));
    hint.textContent = !available ? 'The pond works without audio.' : sound.isMuted ? 'Enable sound when you are ready. Your choice and volume are remembered.' : sound.isScenePaused ? 'The pond is paused. Resume it in Motion settings.' : sound.isPaused ? 'Pond sound is paused.' : 'Sound pauses while this tab is hidden.';
    for (const button of samples) button.disabled = !available || sound.isMuted || sound.isPaused || sound.isScenePaused || sound.volume === 0;
  };
  toggle.onclick = () => { sound.toggleMute(); update(); };
  slider.oninput = () => { sound.setVolume(Number(slider.value) / 100); update(); };
  pause.onclick = () => { sound.setPaused(!sound.isPaused); update(); };
  settings.addEventListener('toggle', update);
  root.addEventListener('focusin', update);
  root.addEventListener('pointerenter', update);

  menu.append(volume, pause);
  if (options.samples) {
    const group = document.createElement('div');
    group.setAttribute('role', 'group'); group.setAttribute('aria-label', 'Preview pond sounds');
    group.style.cssText = 'display:flex;gap:6px;flex-wrap:wrap';
    for (const [name, play] of [
      ['Bird', () => sound.bird()], ['Frog', () => sound.croak()], ['Duck', () => sound.duck()],
      ['Drip', () => sound.drip()], ['Splash', () => sound.splash()],
    ] as const) {
      const button = document.createElement('button'); button.type = 'button'; button.className = 'btn secondary small';
      button.textContent = name; button.setAttribute('aria-label', `Preview ${name.toLowerCase()} sound`);
      button.onclick = () => { sound.start(); play(); update(); };
      samples.push(button); group.append(button);
    }
    menu.append(group);
  }
  menu.append(hint); settings.append(summary, menu); root.append(toggle, settings); update();
  return root;
}
